import {
  Autocomplete,
  Button,
  Checkbox,
  FormControlLabel,
  ListItem,
  Radio,
  RadioGroup,
  Rating,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StyledContainerColumnLeft, StyledContainerRowBetween } from '../../styles/ContainerStyles'
import { Circle, FilePresent, FilterList, RadioButtonUnchecked } from '@mui/icons-material'
import dayjs from 'dayjs'
import Searchbar, { SearchbarRef } from '../../components/Searchbar/Searchbar'
import ReportsProjectsTable from '../../components/Table/Reports/ReportsProjectsTable'
import ReportsUsersTable from '../../components/Table/Reports/ReportsUsersTable'
import { defaultPageSize } from '../../config/defaultPagination'
import ReportPopupData from '../../store/types/utils/ReportPopupUtil'
import { LoadingButton } from '@mui/lab'
import { useGetDictionariesQuery } from '../../store/redux/reducers/dictionarySlice'
import defaultProps from '../../config/defaultProps'
import { Option } from '../../components/Form/Inputs/AutocompleteInput'
import { Box } from '@mui/system'
import LocalStorageTypes from '../../store/types/enums/LocalStorageTypes'
import { ProjectReportOptions, projectsTypeArray } from '../../store/types/enums/ReportType'
import DateRangePickerWithPresets from '../../components/DateRangePickerWithPresets/DateRangePickerWithPresets'
import { useGetTechnologiesQuery } from '../../store/redux/reducers/technologiesSlice'
import TechnologyLogo from '../../components/Logo/TechnologyLogo'
import { Technology } from '../../store/types'
import useReportDownload from './reportDownload'
import ContractEnum from '../../store/types/enums/ContractEnum'
import { reportTiles, ReportTileValue } from '../../store/constants/reportConstants'

type ReportGenerationPanelProps = {
  selectedTile: ReportTileValue | null
}

const EMPLOYMENT_TYPE_SELECT_ID = 'employment-type-select'
const TECHNOLOGIES_AUTOCOMPLETE_ID = 'technologies-autocomplete'

const ReportGenerationPanel = ({ selectedTile }: ReportGenerationPanelProps) => {
  const { t } = useTranslation()

  const theme = useTheme()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))

  const [searchInput, setSearchInput] = useState('')
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(dayjs().startOf('month').utc(true))
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(dayjs().endOf('month').utc(true))
  const [selectedType, setSelectedType] = useState(ProjectReportOptions.Default)
  const [selectedData, setSelectedData] = useState<ReportPopupData[]>([])
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    parseInt(localStorage.getItem(LocalStorageTypes.ReportsTableRows) || defaultPageSize.toString())
  )
  const searchbarRef = useRef<SearchbarRef>(null)
  const [loading, setLoading] = useState<boolean>(false)

  const [page, setPage] = useState(0)
  const employmentRequestProps = useMemo(
    () => ({ ...defaultProps, page: page, group: ContractEnum.EMPLOYMENT_TYPE }),
    [page]
  )
  const { data: employmentType } = useGetDictionariesQuery(employmentRequestProps, {
    skip: selectedTile !== reportTiles.CONTRACT,
  })
  const employmentTypeOptions = useMemo(
    () =>
      employmentType
        ? employmentType.content.map(value => ({
            value: value.key,
            label: value.value,
          }))
        : [],
    [employmentType]
  )
  const [selectedEmploymentTypes, setSelectedEmploymentTypes] = useState<Option[]>([])

  useEffect(() => {
    if (employmentTypeOptions.length > 0) {
      const defaultEmploymentTypes = employmentTypeOptions.filter(
        option => option.value === ContractEnum.MANDATE_CONTRACT || option.value === ContractEnum.WORK_CONTRACT
      )
      setSelectedEmploymentTypes(defaultEmploymentTypes)
    }
  }, [employmentTypeOptions])

  const onSelectBottomScroll = useCallback(() => {
    if (!employmentType?.last) {
      setPage(prev => prev + 1)
    }
  }, [employmentType])

  useEffect(() => {
    if (searchbarRef.current) {
      searchbarRef.current.reset()
    }
    setSearchInput('')
  }, [selectedTile])

  const { data: technologies, isLoading: isTechsLoading } = useGetTechnologiesQuery(
    { ...defaultProps, page: 0, size: 500 },
    { skip: selectedTile !== reportTiles.TECHNOLOGY }
  )
  const [selectedTechnologies, setSelectedTechnologies] = useState<Technology[]>([])
  const [minimumLevel, setMinimumLevel] = useState<number>(1)
  const [maximumLevel, setMaximumLevel] = useState<number>(10)
  const [alternativeAppearance, setAlternativeAppearance] = useState<boolean>(true)
  const [includeCosts, setIncludeCosts] = useState<boolean>(true)

  const updateSelectedData = useCallback((selectedIds: ReportPopupData[]) => {
    setSelectedData(selectedIds)
  }, [])

  const isSelectionInvalid: boolean =
    selectedTile === reportTiles.CONTRACT
      ? false
      : selectedTile === reportTiles.TECHNOLOGY
      ? minimumLevel > maximumLevel
      : selectedData.length === 0

  const { handleDownloadReport } = useReportDownload({
    selectedTile,
    selectedData,
    startDate,
    endDate,
    projectReportType: selectedType,
    selectedEmploymentTypes,
    selectedTechnologies,
    minimumLevel,
    maximumLevel,
    alternativeAppearance,
    includeCosts,
  })

  const [isParentProjectsFilter, setIsParentProjectsFilter] = useState<boolean>(true)

  const isDateRangeInvalid = selectedTile !== reportTiles.TECHNOLOGY && endDate?.isBefore(startDate)
  const displayGenerateReportButtonTooltip = !isSelectionInvalid || isDateRangeInvalid
  const isGenerateReportButtonDisabled = isSelectionInvalid || isDateRangeInvalid

  return (
    <>
      <StyledContainerRowBetween sx={{ flexDirection: isDownSm ? 'column' : 'row' }}>
        <StyledContainerColumnLeft>
          <Typography variant="h3" sx={{ color: 'text.highEmphasis' }}>
            {t('page.reports.generateReport')}
          </Typography>
          <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.mediumEmphasis' }}>
            &nbsp;
          </Typography>
        </StyledContainerColumnLeft>
        <Tooltip title={displayGenerateReportButtonTooltip ? t('page.reports.recordsTileCaption') : ''}>
          <span>
            <LoadingButton
              variant="contained"
              color="primary"
              startIcon={<FilePresent />}
              onClick={() => {
                setLoading(true)
                handleDownloadReport().finally(() => setLoading(false))
              }}
              disabled={isGenerateReportButtonDisabled}
              loading={loading}
              fullWidth={isDownSm}
            >
              {t('page.users.generateReport')}
            </LoadingButton>
          </span>
        </Tooltip>
      </StyledContainerRowBetween>

      {selectedTile !== reportTiles.TECHNOLOGY && (
        <StyledContainerColumnLeft sx={{ gap: 1.5 }}>
          <StyledContainerColumnLeft>
            <Typography variant="h3" sx={{ color: 'text.highEmphasis' }}>
              {t('page.reports.timeFrame')}
            </Typography>
            <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.mediumEmphasis' }}>
              {t('page.reports.timeFrameCaption')}
            </Typography>
          </StyledContainerColumnLeft>
          <DateRangePickerWithPresets
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
          />
        </StyledContainerColumnLeft>
      )}

      <StyledContainerColumnLeft sx={{ gap: 1.5, height: isDownSm ? 'auto' : '100%' }}>
        {selectedTile === reportTiles.PROJECTS && (
          <Stack sx={{ gap: 1.5 }}>
            <Stack>
              <Typography>{t('page.reports.projectReportShouldInclude')}</Typography>
              <RadioGroup value={selectedType} onChange={e => setSelectedType(e.target.value as ProjectReportOptions)}>
                {projectsTypeArray.map(({ type, label }) => (
                  <FormControlLabel
                    key={type}
                    value={type}
                    sx={{
                      color: 'text.main',
                      margin: 0,
                      alignSelf: 'start',
                    }}
                    control={<Radio inputProps={{ 'aria-label': t(label) }} />}
                    label={t(label)}
                    labelPlacement="end"
                  />
                ))}
              </RadioGroup>
            </Stack>
            <FormControlLabel
              sx={{ margin: 0, alignSelf: 'start' }}
              control={
                <Switch
                  checked={includeCosts}
                  onChange={(_, checked) => setIncludeCosts(checked)}
                  inputProps={{ 'aria-label': t('page.reports.includeCosts') }}
                />
              }
              label={t('page.reports.includeCosts')}
            />
          </Stack>
        )}
        <StyledContainerColumnLeft sx={{ height: isDownSm ? 'auto' : '100%' }}>
          {selectedTile === reportTiles.PROJECTS && (
            <>
              <Typography variant="h3" sx={{ color: 'text.highEmphasis' }}>
                {t('page.reports.projects')}
              </Typography>
              <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.mediumEmphasis' }}>
                {t('page.reports.projectsCaption')}
              </Typography>
            </>
          )}

          {selectedTile === reportTiles.USERS && (
            <>
              <Typography variant="h3" sx={{ color: 'text.highEmphasis' }}>
                {t('page.reports.users')}
              </Typography>
              <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.mediumEmphasis' }}>
                {t('page.reports.usersCaption')}
              </Typography>
            </>
          )}

          {selectedTile === reportTiles.CONCRETE_USER && (
            <>
              <Typography variant="h3" sx={{ color: 'text.highEmphasis' }}>
                {t('page.reports.concreteUser')}
              </Typography>
              <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.mediumEmphasis' }}>
                {t('page.reports.concreteUserCaption')}
              </Typography>
            </>
          )}
        </StyledContainerColumnLeft>

        {selectedTile === reportTiles.PROJECTS && (
          <Stack direction={'row'} sx={{ gap: 2 }}>
            <Searchbar
              placeholder={t('components.placeholder.search')}
              handleChange={(value: string) => setSearchInput(value)}
              ref={searchbarRef}
              aria-label={t('components.placeholder.search')}
            />
            <Button
              variant="contained"
              color="secondary"
              disableElevation
              startIcon={isDownSm ? '' : <FilterList />}
              size="medium"
              className={isParentProjectsFilter ? 'selected' : ''}
              onClick={() => setIsParentProjectsFilter(!isParentProjectsFilter)}
            >
              {t('page.reports.filterOnlyParents')}
            </Button>
          </Stack>
        )}

        {selectedTile === reportTiles.CONTRACT && (
          <Autocomplete
            id={EMPLOYMENT_TYPE_SELECT_ID}
            disabled
            multiple
            disableCloseOnSelect
            limitTags={3}
            value={selectedEmploymentTypes}
            onChange={(_, value) => {
              setSelectedEmploymentTypes(value)
            }}
            options={employmentTypeOptions}
            renderOption={(props, option, { selected }) => (
              <ListItem {...props} sx={{ gap: 1 }}>
                <FormControlLabel
                  control={<Checkbox sx={{ p: 0.5 }} checked={selected} inputProps={{ 'aria-label': option.label }} />}
                  label={option.label}
                />
              </ListItem>
            )}
            renderInput={params => (
              <TextField
                {...params}
                id={EMPLOYMENT_TYPE_SELECT_ID}
                label={t('page.reports.selectEmploymentType')}
                aria-label={t('page.reports.selectEmploymentType')}
                size="small"
                fullWidth
                disabled
              />
            )}
            ListboxProps={{
              style: { maxHeight: 200 },
              onScroll: (event: React.SyntheticEvent) => {
                const listboxNode = event.currentTarget
                if (listboxNode.scrollTop + listboxNode.clientHeight === listboxNode.scrollHeight) {
                  onSelectBottomScroll()
                }
              },
            }}
          />
        )}

        {selectedTile === reportTiles.TECHNOLOGY && (
          <Stack sx={{ width: '100%', gap: 2 }}>
            <StyledContainerColumnLeft sx={{ gap: 0.5 }}>
              <Typography variant="h3" sx={{ color: 'text.highEmphasis' }}>
                {t('page.reports.technologies')}
              </Typography>
              <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.mediumEmphasis' }}>
                {t('page.reports.technologiesCaption')}
              </Typography>
            </StyledContainerColumnLeft>
            <Autocomplete
              multiple
              id={TECHNOLOGIES_AUTOCOMPLETE_ID}
              size="small"
              disableCloseOnSelect
              limitTags={3}
              loading={isTechsLoading}
              value={selectedTechnologies}
              onChange={(_, value) => {
                setSelectedTechnologies(value)
              }}
              options={technologies ? technologies.content : []}
              getOptionLabel={option => option.name}
              renderOption={(props, option, { selected }) => (
                <ListItem {...props} sx={{ gap: 1.5 }}>
                  <Checkbox sx={{ p: 0.5, ml: -0.5 }} checked={selected} inputProps={{ 'aria-label': option.name }} />
                  <TechnologyLogo size={24} technology={option} />
                  {option.name}
                </ListItem>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  id={TECHNOLOGIES_AUTOCOMPLETE_ID}
                  label={t('page.users.filtering.technologies')}
                  aria-label={t('page.users.filtering.technologies')}
                  size="small"
                  fullWidth
                />
              )}
              ListboxProps={{
                style: { maxHeight: 200 },
              }}
            />
            <Box
              component="fieldset"
              sx={{
                width: '100%',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                m: 0,
                px: 2,
                py: 1.5,
              }}
            >
              <Typography component="legend" variant="caption" sx={{ px: 1, color: 'text.mediumEmphasis' }}>
                {t('page.reports.technologyLevel')}
              </Typography>
              <Stack
                direction={isDownSm ? 'column' : 'row'}
                sx={{ gap: 3, alignItems: isDownSm ? 'flex-start' : 'center' }}
              >
                <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'text.highEmphasis' }}>
                    {t('page.reports.minimumLevel')}
                  </Typography>
                  <Rating
                    name={'minimum-technology-level'}
                    value={minimumLevel / 2}
                    onChange={(event, newValue) => {
                      const updatedMinimumLevel =
                        newValue === null ? 1 : Math.max(1, Math.min(10, Math.round(newValue * 2)))
                      setMinimumLevel(updatedMinimumLevel)
                      if (updatedMinimumLevel > maximumLevel) {
                        setMaximumLevel(updatedMinimumLevel)
                      }
                    }}
                    getLabelText={(value: number) => `${t('page.reports.minimumLevel')} ${Math.round(value * 2)}`}
                    precision={0.5}
                    icon={
                      <Circle sx={{ width: 14, mr: 0.25, color: theme.palette.primary.main }} aria-hidden={'false'} />
                    }
                    emptyIcon={
                      <RadioButtonUnchecked
                        sx={{ width: 14, mr: 0.25, color: theme.palette.primary.main }}
                        aria-hidden={'false'}
                      />
                    }
                    aria-label={t('page.reports.minimumLevel')}
                  />
                </Stack>
                <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'text.highEmphasis' }}>
                    {t('page.reports.maximumLevel')}
                  </Typography>
                  <Rating
                    name={'maximum-technology-level'}
                    value={maximumLevel / 2}
                    onChange={(event, newValue) => {
                      const updatedMaximumLevel =
                        newValue === null ? 10 : Math.max(1, Math.min(10, Math.round(newValue * 2)))
                      setMaximumLevel(updatedMaximumLevel)
                      if (updatedMaximumLevel < minimumLevel) {
                        setMinimumLevel(updatedMaximumLevel)
                      }
                    }}
                    getLabelText={(value: number) => `${t('page.reports.maximumLevel')} ${Math.round(value * 2)}`}
                    precision={0.5}
                    icon={
                      <Circle sx={{ width: 14, mr: 0.25, color: theme.palette.primary.main }} aria-hidden={'false'} />
                    }
                    emptyIcon={
                      <RadioButtonUnchecked
                        sx={{ width: 14, mr: 0.25, color: theme.palette.primary.main }}
                        aria-hidden={'false'}
                      />
                    }
                    aria-label={t('page.reports.maximumLevel')}
                  />
                </Stack>
              </Stack>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={alternativeAppearance}
                  onChange={(_, value) => setAlternativeAppearance(value)}
                  inputProps={{ 'aria-label': t('page.reports.alternativeAppearance') }}
                />
              }
              label={t('page.reports.alternativeAppearance')}
            />

            <StyledContainerColumnLeft sx={{ gap: 1.5 }}>
              <Typography variant="h3" sx={{ color: 'text.highEmphasis' }}>
                {t('page.reports.users')}
              </Typography>
              <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.mediumEmphasis' }}>
                {t('page.reports.usersCaption')}
              </Typography>
              <Stack direction={'row'} sx={{ gap: 2 }}>
                <Searchbar
                  placeholder={t('components.placeholder.search')}
                  handleChange={(value: string) => setSearchInput(value)}
                  ref={searchbarRef}
                  aria-label={t('components.placeholder.search')}
                />
              </Stack>
            </StyledContainerColumnLeft>
          </Stack>
        )}

        <Box sx={{ width: '100%' }}>
          {selectedTile === reportTiles.PROJECTS && (
            <ReportsProjectsTable
              key={isParentProjectsFilter ? 'parent-projects' : 'all-projects'}
              searchInput={searchInput}
              updateSelectedData={updateSelectedData}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              isParentProjectsFilter={isParentProjectsFilter}
            />
          )}
          {(selectedTile === reportTiles.USERS || selectedTile === reportTiles.CONCRETE_USER) && (
            <Stack sx={{ gap: 2 }}>
              <Searchbar
                placeholder={t('components.placeholder.search')}
                handleChange={(value: string) => setSearchInput(value)}
                ref={searchbarRef}
                aria-label={t('components.placeholder.search')}
              />
              <ReportsUsersTable
                searchInput={searchInput}
                concreteUser={selectedTile === reportTiles.CONCRETE_USER}
                updateSelectedData={updateSelectedData}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
              />
            </Stack>
          )}
          {selectedTile === reportTiles.TECHNOLOGY && (
            <ReportsUsersTable
              searchInput={searchInput}
              concreteUser={false}
              updateSelectedData={updateSelectedData}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
            />
          )}
        </Box>
      </StyledContainerColumnLeft>
    </>
  )
}

export default ReportGenerationPanel
